import { DateTimePicker as MuiDateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers-pro';
import FormLabel from './FormLabel';
import { useMemo, useRef } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import ClearInputAdornment from './ClearInputAdornment';
import { Dayjs } from 'dayjs';
import classNames from 'classnames/bind';
import { AppInputCustomProps, CustomFieldProps } from './types';
import DisabledTooltip from '@components/DisabledTooltip';
import { dayjsNow, parseDayJs, parseDayJsFromDateTimeInput } from '@/Infrastructure/Utilities/dayjs.type';
import { CustomDateRangePickerAdornment } from './DateRangePicker';

import styles from './Form.module.css';
const styleContext = classNames.bind(styles);

/**
 * Wrapper around MUI DateTimePicker to provide application-wide default behaviors.
 */
export default function DateTimePicker({ customProps, ...props }: DateTimePickerProps & AppInputCustomProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const dynamicClasses = styleContext({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: props.disabled,
    empty: !props.value,
    [props.className ?? '']: !!props.className
  });

  const blurEventHandler = () => {
    const rawValue = inputRef.current?.value || '';

    // If it's already a valid dayjs obj return without doing anything
    if (parseDayJs(rawValue)?.isValid()) {
      return;
    }

    const dayJsValue = parseDayJsFromDateTimeInput(rawValue);

    if (dayJsValue?.isValid() && props.onChange) {
      props.onChange(dayJsValue, { validationError: null, source: 'field' });
    }
  };

  return (
    <DisabledTooltip
      message={customProps.disabledReason}
      className={customProps.disabledClass}>
      <MuiDateTimePicker
        {...props}
        className={dynamicClasses}
        label={
          <FormLabel
            text={customProps.labelText}
            error={customProps.isError}
            errorText={customProps.errorText}
            required={customProps.required}
          />
        }
        disabled={props.disabled}
        slotProps={{
          textField: {
            inputRef: inputRef,
            variant: customProps.variant,
            onBlur: blurEventHandler,
            error: customProps.isError,
            slotProps: {
              input: {
                className: styles.input,
                slotProps: {
                  root: {
                    className: styleContext({
                      root: true,
                      'date-time-picker': true
                    })
                  }
                },
                startAdornment: <CustomDateRangePickerAdornment />,
                endAdornment: (
                  <>
                    {
                      <ClearInputAdornment
                        onClear={customProps.onClear}
                        inputValue={props.value}
                        inputIsDisabled={props.disabled}
                      />
                    }
                    {!props.disabled && customProps.nowOnClick && props.value === null && (
                      <InputAdornment position='end'>
                        <IconButton
                          title='Use Current Timestamp'
                          onClick={() => {
                            if (customProps.nowOnClick) {
                              customProps.nowOnClick(dayjsNow());
                            } else {
                              console.log('DateTime input Now click event is not setup.');
                            }
                          }}
                          tabIndex={-1}>
                          <MoreTimeIcon fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                )
              }
            }
          }
        }}
      />
    </DisabledTooltip>
  );
}

function asDateTimePicker() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<Dayjs | null>(field.name);

    const { dirty } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        if (value && initialValue) {
          // Determine if the dayjs value is different
          return value.diff(initialValue) !== 0;
        } else if (!value && !initialValue) {
          // Input was null, and is still null
          return false;
        } else {
          // It was null, and now it is not or vice versa
          return true;
        }
      }
      return false;
    }, [value, initialValue, dirty]);

    return (
      <DateTimePicker
        name={field.name}
        className={props.className}
        value={field.value as Dayjs}
        onChange={(value) => {
          void form.setFieldValue(field.name, value, true);
        }}
        disabled={form.isSubmitting || props.disabled}
        customProps={{
          labelText: props.label ?? '',
          onClear: () => {
            void form.setFieldTouched(field.name, true);
            void form.setFieldValue(field.name, null);
          },
          isDirty: isDirty,
          isError: Boolean(form.dirty && error),
          errorText: error,
          required: props.required,
          disabledReason: props.disabledReason,
          disabledClass: props.disabledTooltipClass,
          nowOnClick: props.nowBtn
            ? (value) => {
                void form.setFieldValue(field.name, value, true);
              }
            : undefined
        }}
      />
    );
  };
}

export const FormikDateTimePicker = asDateTimePicker();
